import { MenuOutlined } from '@ant-design/icons';
import { useAuth0 } from '@auth0/auth0-react';
import {
    Button,
    ConfigProvider,
    Drawer,
    Grid,
    Layout,
    notification,
    Row,
    Typography,
    theme,
} from 'antd';
import React, { useEffect, useState } from 'react';
import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom';

import { Roles } from '@clh/api-client';
import { useRequestedLocation, useRoles } from '@clh/ui';

import CurrentProfileProvider from './hooks/use-current-profile/current-profile-provider';
import ToastProvider from './hooks/use-toast/toast-provider';
import { Nav } from './nav';
import Spinner from './spinner/spinner';

const { Sider } = Layout;
const { useBreakpoint } = Grid;
const { ENVIRONMENT_NAME, GIT_COMMIT } = ENV_CONFIG_VARS;

const App: React.FC = () => {
    const {
        token: { colorBgContainer },
    } = theme.useToken();

    const { isLoading, isAuthenticated } = useAuth0();

    const roles = useRoles();
    const navigate = useNavigate();
    const location = useLocation();
    const breakpoints = useBreakpoint();
    const isMobile = !breakpoints.md;
    const [navOpen, setNavOpen] = useState(false);

    // Close nav drawer on route change
    useEffect(() => {
        setNavOpen(false);
    }, [location.pathname]);

    const { requestedLocation, clearRequestedLocation } = useRequestedLocation([
        '/logout',
    ]);

    useEffect(() => {
        if (isLoading || !isAuthenticated || roles.isLoading) {
            return;
        }

        if (!roles.roles.includes(Roles.PlatformAdministrator)) {
            navigate('/unauthorized');
            return;
        }

        if (requestedLocation) {
            clearRequestedLocation();
            navigate(requestedLocation, { replace: true });
        }
    }, [
        isLoading,
        isAuthenticated,
        roles.isLoading,
        roles.roles,
        requestedLocation,
    ]);

    const [toast, toastContextHolder] = notification.useNotification({
        placement: 'topLeft',
    });
    return (
        <ConfigProvider
            theme={{
                token: {
                    colorPrimary: '#4b7fb1',
                },
                components: {
                    Layout: {
                        colorBgHeader: '#fff',
                        siderBg: '#fff',
                    },
                    Menu: {
                        itemBg: '#fff',
                        itemColor: '#000',
                        itemHoverBg: '#999',
                        itemSelectedBg: '#1c3f60',
                        subMenuItemBg: '#e0e0e0',
                    },
                },
            }}
        >
            <Layout style={{ height: '100vh' }}>
                <CurrentProfileProvider>
                    {toastContextHolder}
                    <ToastProvider notification={toast}>
                        {!isAuthenticated ||
                        roles.isLoading ||
                        requestedLocation ? (
                            <Row
                                justify="center"
                                align="middle"
                                style={{ minHeight: '100vh' }}
                            >
                                <Spinner size="large" />
                            </Row>
                        ) : (
                            <>
                                {isMobile ? (
                                    <Drawer
                                        placement="left"
                                        open={navOpen}
                                        onClose={() => setNavOpen(false)}
                                        width={220}
                                        styles={{
                                            body: { padding: 0 },
                                        }}
                                    >
                                        <Link to="/">
                                            <div
                                                style={{
                                                    textAlign: 'center',
                                                    paddingTop: 12,
                                                }}
                                            >
                                                <img
                                                    src={require('./saasquatch.png')}
                                                    className="saasquatch-logo"
                                                />
                                            </div>
                                        </Link>
                                        <Nav />
                                        <Typography.Text
                                            italic
                                            style={{
                                                color: '#aaa',
                                                display: 'block',
                                                textAlign: 'center',
                                                padding: 8,
                                            }}
                                        >
                                            {ENVIRONMENT_NAME} &nbsp;
                                            {GIT_COMMIT}
                                        </Typography.Text>
                                    </Drawer>
                                ) : (
                                    <Sider className="admin-sider">
                                        <Link to="/">
                                            <h3
                                                style={{
                                                    color: colorBgContainer,
                                                    textAlign: 'center',
                                                    paddingTop: 12,
                                                }}
                                            >
                                                <img
                                                    src={require('./saasquatch.png')}
                                                    className="saasquatch-logo"
                                                />
                                            </h3>
                                        </Link>
                                        <Nav />
                                        <Typography.Text
                                            italic
                                            className="admin-sider-version"
                                        >
                                            {ENVIRONMENT_NAME} &nbsp;
                                            {GIT_COMMIT}
                                        </Typography.Text>
                                    </Sider>
                                )}
                                <Layout
                                    style={{
                                        flex: 1,
                                        minWidth: 0,
                                        overflow: 'auto',
                                    }}
                                >
                                    {isMobile &&
                                        !location.pathname.startsWith(
                                            '/chat'
                                        ) && (
                                            <div
                                                style={{
                                                    position: 'sticky',
                                                    top: 0,
                                                    zIndex: 10,
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: 8,
                                                    padding: '8px 12px',
                                                    background: '#fff',
                                                    borderBottom:
                                                        '1px solid #e8e8e8',
                                                }}
                                            >
                                                <Button
                                                    icon={<MenuOutlined />}
                                                    onClick={() =>
                                                        setNavOpen(true)
                                                    }
                                                />
                                                <Link to="/">
                                                    <img
                                                        src={require('./saasquatch.png')}
                                                        className="saasquatch-logo"
                                                        style={{
                                                            height: 32,
                                                            width: 'auto',
                                                        }}
                                                    />
                                                </Link>
                                            </div>
                                        )}
                                    <Outlet />
                                </Layout>
                            </>
                        )}
                    </ToastProvider>
                </CurrentProfileProvider>
            </Layout>
        </ConfigProvider>
    );
};

export default App;
